import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import QRCode from "qrcode";
import LogbookEntryForm from "../../components/fire-logbook/LogbookEntryForm";
import {
  correctLogbookEntry,
  createLogbookEntry,
  deleteInstallation,
  getFireSafetyModuleSettings,
  getInstallation,
  getInstallationByQrToken,
  getInstallationLogbook,
} from "../../services/fireSafetyLogbookApi";
import { listTenantDocuments } from "../../services/documentLibraryService";
import { useUser } from "../../context/UserContext";

const TABS = [
  {
    key: "logboek",
    label: "Logboek",
    title: "Operationeel logboek",
    description: "Append-only logboekregels met correcties, bijlagen en auditcontext per installatie.",
  },
  {
    key: "werkbonnen",
    label: "Werkbonnen",
    title: "Werkbonregister",
    description: "Actieve en historische werkbonnen in een compacte dossierweergave.",
  },
  {
    key: "documenten",
    label: "Documenten",
    title: "Documentdossier",
    description: "Centrale documentregistratie en gekoppelde installatiedata binnen hetzelfde dossier.",
  },
  {
    key: "inspecties",
    label: "Inspecties",
    title: "Inspectieoverzicht",
    description: "Inspectielogboek en inspectierapporten gekoppeld aan deze installatie.",
  },
  {
    key: "historie",
    label: "Historie",
    title: "Volledige historie",
    description: "Chronologische dossiergeschiedenis van logboek, werkbonnen en documenten.",
  },
  {
    key: "toegang",
    label: "Toegang",
    title: "Toegang en Service-ID",
    description: "Service-ID, QR-route, toegangstoestand en snelle beheeracties.",
  },
];

function getRoleNames(user) {
  const names = new Set();
  if (user?.rol) names.add(String(user.rol).trim().toLowerCase());
  if (user?.role) names.add(String(user.role).trim().toLowerCase());
  for (const role of user?.roles || []) {
    if (!role) continue;
    if (typeof role === "string") names.add(role.trim().toLowerCase());
    if (typeof role === "object" && (role.name || role.naam)) {
      names.add(String(role.name || role.naam).trim().toLowerCase());
    }
  }
  return [...names];
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatType(value) {
  const type = String(value || "").trim().toLowerCase();
  if (!type) return "-";
  const labels = {
    bmi: "BMI",
    oai: "OAI",
    sprinkler: "Sprinkler",
    blusgas: "Blusgas",
    ontruiming: "Ontruiming",
    overig: "Overig",
  };
  return labels[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

function formatAccessState(installation) {
  if (installation?.qrEnabled) return "Actief";
  return "Nog niet actief";
}

function formatStatusLabel(value) {
  const input = String(value || "").trim().toLowerCase();
  if (!input) return "-";
  return input
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatEntryType(value) {
  const labels = {
    inspectie: "Inspectie",
    onderhoud: "Onderhoud",
    beheer: "Beheer",
    storing: "Storing",
    test: "Test",
    wijziging: "Wijziging",
  };
  return labels[String(value || "").trim().toLowerCase()] || "Logboekregel";
}

function formatDocumentType(value) {
  const labels = {
    general: "Algemeen",
    workorder: "Werkbon",
    timesheet: "Mandagstaat",
    inspection_report: "Inspectierapport",
    certificate: "Certificaat",
    drawing: "Tekening",
    revision: "Revisie",
    toolbox: "Toolbox",
    safety_instruction: "Veiligheidsinstructie",
    permit: "Vergunning",
    customer_document: "Klantdocument",
    project_document: "Projectdocument",
    asset_document: "Installatiedocument",
    template: "Template",
    other: "Overig",
  };
  return labels[String(value || "").trim().toLowerCase()] || formatStatusLabel(value);
}

function formatLocationLabel(installation) {
  return (
    installation?.bagAddress?.formattedAddress ||
    installation?.locatie ||
    installation?.locationLabel ||
    "Locatie onbekend"
  );
}

function formatGebruiksdoelen(values) {
  if (!Array.isArray(values) || values.length === 0) return "-";
  return values.join(", ");
}

function formatArea(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "-";
  return `${amount.toLocaleString("nl-NL")} m2`;
}

function getAttachmentName(attachment) {
  return attachment?.naam || attachment?.name || attachment?.originalName || "Bijlage";
}

function getDocumentCategoryLabel(item) {
  return item?.category?.label || item?.category?.key || "-";
}

function getDocumentVisibilityLabel(item) {
  const visibility = item?.visibility || {};
  const labels = [];
  if (visibility.projectleider) labels.push("projectleider");
  if (visibility.tenantAdmin) labels.push("tenant-admin");
  if (visibility.inspecteur) labels.push("inspecteur");
  if (visibility.monteur) labels.push("monteur");
  if (visibility.customerPortal) labels.push("klantportaal");
  return labels.length ? labels.join(", ") : "-";
}

function getHistoryRows(entries, openWorkorders, historicalWorkorders, documents) {
  const logRows = (entries || []).map((entry) => ({
    id: `entry-${entry?._id || entry?.createdAt || Math.random()}`,
    date: entry?.createdAt || entry?.performedAt || null,
    source: "Logboek",
    title: formatEntryType(entry?.entryType),
    detail: entry?.description || "-",
    status: formatStatusLabel(entry?.status || "active"),
    actor: entry?.createdBy?.naam || entry?.createdBy?.email || "-",
  }));

  const workorderRows = [...(openWorkorders || []), ...(historicalWorkorders || [])].map((item) => ({
    id: `workorder-${item?._id || item?.id || Math.random()}`,
    date: item?.updatedAt || item?.plannedDate || item?.createdAt || null,
    source: "Werkbon",
    title: item?.werkbonNummer || item?.nummer || item?._id || "-",
    detail: item?.omschrijving || "Geen omschrijving",
    status: formatStatusLabel(item?.status),
    actor: "-",
  }));

  const documentRows = (documents || []).map((item) => ({
    id: `document-${item?._id || Math.random()}`,
    date: item?.updatedAt || item?.createdAt || null,
    source: "Document",
    title: item?.title || "-",
    detail: formatDocumentType(item?.documentType),
    status: formatStatusLabel(item?.status),
    actor: "-",
  }));

  return [...logRows, ...workorderRows, ...documentRows].sort((a, b) => {
    const left = new Date(a.date || 0).getTime();
    const right = new Date(b.date || 0).getTime();
    return right - left;
  });
}

function statusTone(value) {
  const input = String(value || "").trim().toLowerCase();
  if (["actief", "active", "goedgekeurd", "gereed", "afgerond", "closed"].includes(input)) {
    return "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200";
  }
  if (["corrected", "gecorrigeerd", "ter_controle", "in_behandeling", "ingepland"].includes(input)) {
    return "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200";
  }
  if (["vervallen", "gearchiveerd", "archived", "gesloten"].includes(input)) {
    return "bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200";
  }
  return "bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200";
}

export default function InstallationDetailPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState("logboek");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [moduleEnabled, setModuleEnabled] = useState(false);
  const [installation, setInstallation] = useState(null);
  const [assets, setAssets] = useState([]);
  const [entries, setEntries] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [openPlannedWorkorders, setOpenPlannedWorkorders] = useState([]);
  const [historicalLogbookWorkorders, setHistoricalLogbookWorkorders] = useState([]);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");

  const canWrite = useMemo(() => {
    const roleNames = getRoleNames(user);
    return roleNames.some((role) =>
      ["projectleider", "tenantadmin", "platformadmin", "beheerder", "monteur", "onderhoudsmonteur"].includes(role)
    );
  }, [user]);

  const accessUrl = useMemo(() => {
    if (!installation?.qrToken || typeof window === "undefined") return "";
    return `${window.location.origin}/i/${installation.qrToken}`;
  }, [installation?.qrToken]);

  const inspectionEntries = useMemo(
    () => entries.filter((entry) => String(entry?.entryType || "").trim().toLowerCase() === "inspectie"),
    [entries]
  );

  const inspectionDocuments = useMemo(
    () =>
      documents.filter(
        (item) => String(item?.documentType || "").trim().toLowerCase() === "inspection_report"
      ),
    [documents]
  );

  const historyRows = useMemo(
    () => getHistoryRows(entries, openPlannedWorkorders, historicalLogbookWorkorders, documents),
    [documents, entries, historicalLogbookWorkorders, openPlannedWorkorders]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || "");
    const requestedTab = String(searchParams.get("tab") || "logboek").trim().toLowerCase();
    if (TABS.some((tab) => tab.key === requestedTab)) {
      setActiveTab(requestedTab);
    } else {
      setActiveTab("logboek");
    }
  }, [location.search]);

  useEffect(() => {
    let active = true;

    async function run() {
      try {
        if (!active) return;
        setLoading(true);
        setDocumentsLoading(true);
        setError("");
        setMessage("");
        setDocumentError("");

        const moduleState = await getFireSafetyModuleSettings();
        if (!active) return;
        setModuleEnabled(Boolean(moduleState.enabled));

        if (!moduleState.enabled) {
          setInstallation(null);
          setAssets([]);
          setEntries([]);
          setDocuments([]);
          setOpenPlannedWorkorders([]);
          setHistoricalLogbookWorkorders([]);
          return;
        }

        const resolvedInstallation = params.qrToken
          ? await getInstallationByQrToken(params.qrToken)
          : (await getInstallation(params.id)).installation;

        if (!resolvedInstallation?._id) {
          throw new Error("Installatie niet gevonden");
        }

        const installationRes = await getInstallation(resolvedInstallation._id);
        let logbookEntries = Array.isArray(installationRes?.logbookEntries)
          ? installationRes.logbookEntries
          : [];
        if (!logbookEntries.length) {
          logbookEntries = await getInstallationLogbook(resolvedInstallation._id);
        }

        let nextDocuments = [];
        let nextDocumentError = "";
        try {
          const documentRes = await listTenantDocuments({
            assetId: resolvedInstallation._id,
          });
          nextDocuments = Array.isArray(documentRes?.items) ? documentRes.items : [];
        } catch (documentErr) {
          const status = Number(documentErr?.response?.status || 0);
          nextDocumentError =
            status === 403
              ? "Geen toegang tot documentbibliotheek voor deze installatie."
              : documentErr?.response?.data?.message || "Documenten laden mislukt";
        }

        if (!active) return;
        setInstallation(installationRes.installation || resolvedInstallation);
        setAssets(Array.isArray(installationRes.assets) ? installationRes.assets : []);
        setEntries(Array.isArray(logbookEntries) ? logbookEntries : []);
        setDocuments(nextDocuments);
        setDocumentError(nextDocumentError);
        setOpenPlannedWorkorders(
          Array.isArray(installationRes.openPlannedWorkorders)
            ? installationRes.openPlannedWorkorders
            : []
        );
        setHistoricalLogbookWorkorders(
          Array.isArray(installationRes.historicalLogbookWorkorders)
            ? installationRes.historicalLogbookWorkorders
            : []
        );
      } catch (err) {
        if (!active) return;
        setError(err?.response?.data?.message || err.message || "Installatie laden mislukt");
      } finally {
        if (active) {
          setLoading(false);
          setDocumentsLoading(false);
        }
      }
    }

    run();
    return () => {
      active = false;
    };
  }, [params.id, params.qrToken]);

  useEffect(() => {
    let active = true;

    async function renderQr() {
      if (!accessUrl) {
        setQrCodeUrl("");
        return;
      }

      try {
        const nextUrl = await QRCode.toDataURL(accessUrl, {
          width: 220,
          margin: 1,
          color: {
            dark: "#0f172a",
            light: "#ffffffff",
          },
        });
        if (active) {
          setQrCodeUrl(nextUrl);
        }
      } catch (err) {
        if (active) {
          setQrCodeUrl("");
        }
      }
    }

    renderQr();
    return () => {
      active = false;
    };
  }, [accessUrl]);

  async function handleCreateEntry(payload) {
    if (!installation?._id) return;
    try {
      setSubmitting(true);
      setError("");
      setMessage("");
      const entry = await createLogbookEntry(installation._id, payload);
      setEntries((prev) => [entry, ...prev]);
      setMessage("Logboekregel opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "Logboekregel opslaan mislukt");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCorrectEntry(entry) {
    const description = window.prompt(
      "Voer de correctietekst in. De originele regel blijft behouden.",
      `Correctie op ${entry.entryType}: `
    );
    if (!description) return;

    try {
      setSubmitting(true);
      setError("");
      setMessage("");
      const corrected = await correctLogbookEntry(entry._id, {
        entryType: entry.entryType,
        description,
        attachments: [],
      });
      setEntries((prev) =>
        [corrected, ...prev.map((item) => (item._id === entry._id ? { ...item, status: "corrected" } : item))]
      );
      setMessage("Correctie opgeslagen.");
    } catch (err) {
      setError(err?.response?.data?.message || "Correctie opslaan mislukt");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteInstallation() {
    if (!installation?._id || deleting) return;
    const ok = window.confirm(
      `Weet je zeker dat je installatie "${installation.naam || installation.name || "Onbekend"}" wilt verwijderen?`
    );
    if (!ok) return;

    try {
      setDeleting(true);
      setError("");
      await deleteInstallation(installation._id);
      navigate("/pl/installaties", {
        replace: true,
        state: { successMessage: "Installatie verwijderd." },
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Installatie verwijderen mislukt");
    } finally {
      setDeleting(false);
    }
  }

  function handleTabChange(tabKey) {
    setActiveTab(tabKey);
    const searchParams = new URLSearchParams(location.search || "");
    if (tabKey === "logboek") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", tabKey);
    }

    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );
  }

  async function handleCopy(value, successText) {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setError("");
      setMessage(successText);
    } catch (err) {
      setError("Kopieren naar klembord mislukt.");
    }
  }

  if (loading) {
    return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-sm text-slate-500 shadow-sm">Laden...</div>;
  }

  if (!moduleEnabled) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-sm text-amber-900">
        De module Installaties &amp; Logboek staat uit voor deze tenant.
      </div>
    );
  }

  if (!installation) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-sm text-rose-700">
        {error || "Installatie niet gevonden"}
      </div>
    );
  }

  const activeTabConfig = TABS.find((tab) => tab.key === activeTab) || TABS[0];
  const installationName = installation.naam || installation.name || "Installatie";
  const serviceStatus = formatStatusLabel(installation.serviceObjectStatus || formatAccessState(installation));
  const identityFields = [
    { label: "Type", value: formatType(installation.type) },
    { label: "Locatie", value: formatLocationLabel(installation) },
    { label: "Service-ID", value: installation.serviceId || "-" },
    { label: "Toegang", value: formatAccessState(installation) },
    { label: "BAG object", value: installation?.bagAddress?.nummeraanduidingIdentificatie || "-" },
    { label: "Laatste mutatie", value: formatDate(installation.updatedAt) },
  ];

  return (
    <div className="space-y-4">
      <header className="rounded-[1.75rem] border border-slate-200 bg-white px-5 py-5 shadow-sm sm:px-6">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              <Link to="/pl/installaties" className="rounded-full border border-slate-200 px-2.5 py-1 text-slate-600 hover:border-slate-300 hover:text-slate-900">
                Installatieregister
              </Link>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-600">
                Technisch dossier
              </span>
            </div>
            <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
              {installationName}
            </h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600 sm:text-base">
              Dossierweergave met installatie-identiteit, logboek, werkbonnen, documenten, inspecties en toegang op een vaste plaats.
            </p>
          </div>

          <dl className="grid gap-x-6 gap-y-4 text-sm text-slate-700 sm:grid-cols-2 xl:min-w-[440px] xl:grid-cols-3">
            {identityFields.map((field) => (
              <div key={field.label} className="min-w-0 border-l border-slate-200 pl-4">
                <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">{field.label}</dt>
                <dd className="mt-1 break-words font-medium text-slate-900">{field.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </header>

      {message ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {message}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <section className="sticky top-4 z-20 rounded-[1.5rem] border border-slate-900 bg-slate-950 px-4 py-4 text-white shadow-lg">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <Link
              to="/pl/installaties"
              className="inline-flex items-center rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-100 transition hover:border-slate-500 hover:bg-slate-900"
            >
              Terug
            </Link>
            <button
              type="button"
              onClick={() => handleTabChange("logboek")}
              className="inline-flex items-center rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-100 transition hover:border-slate-500 hover:bg-slate-900"
            >
              Logboek
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("documenten")}
              className="inline-flex items-center rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-100 transition hover:border-slate-500 hover:bg-slate-900"
            >
              Documenten
            </button>
            {accessUrl ? (
              <a
                href={accessUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-lg bg-emerald-500 px-3 py-2 text-sm font-medium text-slate-950 transition hover:bg-emerald-400"
              >
                Open QR-route
              </a>
            ) : null}
            {(accessUrl || installation.qrToken) ? (
              <button
                type="button"
                onClick={() => handleCopy(accessUrl || installation.qrToken, "Toegangsgegevens gekopieerd.")}
                className="inline-flex items-center rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-100 transition hover:border-slate-500 hover:bg-slate-900"
              >
                Kopieer toegang
              </button>
            ) : null}
            {canWrite ? (
              <button
                type="button"
                onClick={handleDeleteInstallation}
                disabled={deleting}
                className="inline-flex items-center rounded-lg border border-rose-500/60 px-3 py-2 text-sm font-medium text-rose-100 transition hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {deleting ? "Verwijderen..." : "Verwijderen"}
              </button>
            ) : null}
          </div>

          <div className="text-xs uppercase tracking-[0.16em] text-slate-400">
            {assets.length} assets · {documents.length} documenten · {entries.length} logboekregels
          </div>
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => handleTabChange(tab.key)}
              className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition ${
                activeTab === tab.key
                  ? "bg-white text-slate-950"
                  : "bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
        <section className="min-w-0 rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              {activeTabConfig.label}
            </p>
            <h2 className="mt-2 text-xl font-semibold text-slate-950">{activeTabConfig.title}</h2>
            <p className="mt-2 text-sm text-slate-600">{activeTabConfig.description}</p>
          </div>

          <div className="px-5 py-5 sm:px-6">
            {activeTab === "logboek" ? (
              <div className="space-y-6">
                {canWrite ? (
                  <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">
                        Nieuwe logboekregel
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Nieuwe regels worden toegevoegd; bestaande regels worden alleen gecorrigeerd.
                      </p>
                    </div>
                    <LogbookEntryForm onSubmit={handleCreateEntry} submitting={submitting} />
                  </section>
                ) : null}

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 text-sm">
                      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        <tr>
                          <th className="px-4 py-3">Datum</th>
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3">Omschrijving</th>
                          <th className="px-4 py-3">Gebruiker</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Bijlagen</th>
                          <th className="px-4 py-3 text-right">Actie</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white">
                        {entries.length ? (
                          entries.map((entry) => (
                            <tr key={entry._id || `${entry.createdAt}-${entry.description}`}>
                              <td className="px-4 py-3 text-slate-600">{formatDate(entry.createdAt || entry.performedAt)}</td>
                              <td className="px-4 py-3 font-medium text-slate-900">{formatEntryType(entry.entryType)}</td>
                              <td className="px-4 py-3 text-slate-700">{entry.description || "-"}</td>
                              <td className="px-4 py-3 text-slate-600">
                                {entry?.createdBy?.naam || entry?.createdBy?.email || "-"}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(entry.status)}`}>
                                  {formatStatusLabel(entry.status || "active")}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {Array.isArray(entry.attachments) && entry.attachments.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {entry.attachments.map((attachment, index) => (
                                      <a
                                        key={`${entry._id || "entry"}-attachment-${index}`}
                                        href={attachment?.url || "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                                      >
                                        {getAttachmentName(attachment)}
                                      </a>
                                    ))}
                                  </div>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                {canWrite ? (
                                  <button
                                    type="button"
                                    onClick={() => handleCorrectEntry(entry)}
                                    className="inline-flex rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                                  >
                                    Corrigeer
                                  </button>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                              Nog geen logboekregels voor deze installatie.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : null}

            {activeTab === "werkbonnen" ? (
              <div className="space-y-6">
                <DossierTable
                  title="Open en geplande werkbonnen"
                  count={openPlannedWorkorders.length}
                  emptyMessage="Geen open of geplande werkbonnen."
                  columns={["Werkbon", "Omschrijving", "Status", "Laatste wijziging", ""]} 
                  rows={openPlannedWorkorders.map((item) => (
                    <tr key={`open-${item._id || item.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {item.werkbonNummer || item.nummer || item._id || "-"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{item.omschrijving || "Geen omschrijving"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(item.status)}`}>
                          {formatStatusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatDate(item.updatedAt || item.plannedDate || item.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/werkbon/${item._id || item.id}`}
                          className="inline-flex rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                />

                <DossierTable
                  title="Historische werkbonnen"
                  count={historicalLogbookWorkorders.length}
                  emptyMessage="Nog geen historische werkbonnen."
                  columns={["Werkbon", "Omschrijving", "Status", "Laatste wijziging", ""]}
                  rows={historicalLogbookWorkorders.map((item) => (
                    <tr key={`history-${item._id || item.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {item.werkbonNummer || item.nummer || item._id || "-"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{item.omschrijving || "Geen omschrijving"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(item.status)}`}>
                          {formatStatusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {formatDate(item.updatedAt || item.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to={`/werkbon/${item._id || item.id}`}
                          className="inline-flex rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                />
              </div>
            ) : null}

            {activeTab === "documenten" ? (
              <div className="space-y-6">
                {documentError ? (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {documentError}
                  </div>
                ) : null}

                <DossierTable
                  title="Documentbibliotheek"
                  count={documents.length}
                  loading={documentsLoading}
                  emptyMessage="Nog geen documenten gekoppeld aan deze installatie."
                  columns={["Titel", "Type", "Categorie", "Status", "Zichtbaarheid", "Laatste wijziging"]}
                  rows={documents.map((item) => (
                    <tr key={item._id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{item.title || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{formatDocumentType(item.documentType)}</td>
                      <td className="px-4 py-3 text-slate-700">{getDocumentCategoryLabel(item)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(item.status)}`}>
                          {formatStatusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{getDocumentVisibilityLabel(item)}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(item.updatedAt || item.createdAt)}</td>
                    </tr>
                  ))}
                />

                <DossierTable
                  title="Gekoppelde assets"
                  count={assets.length}
                  emptyMessage="Nog geen assets gekoppeld aan deze installatie."
                  columns={["Asset", "Type", "Locatie", "Aangemaakt"]}
                  rows={assets.map((asset) => (
                    <tr key={asset._id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{asset.naam || asset.name || "-"}</td>
                      <td className="px-4 py-3 text-slate-700">{formatType(asset.type)}</td>
                      <td className="px-4 py-3 text-slate-700">{asset.locatie || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(asset.createdAt)}</td>
                    </tr>
                  ))}
                />
              </div>
            ) : null}

            {activeTab === "inspecties" ? (
              <div className="space-y-6">
                <DossierTable
                  title="Inspectielogboek"
                  count={inspectionEntries.length}
                  emptyMessage="Nog geen inspectieregels in het logboek."
                  columns={["Datum", "Beschrijving", "Gebruiker", "Status"]}
                  rows={inspectionEntries.map((entry) => (
                    <tr key={entry._id}>
                      <td className="px-4 py-3 text-slate-600">{formatDate(entry.createdAt || entry.performedAt)}</td>
                      <td className="px-4 py-3 text-slate-700">{entry.description || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">
                        {entry?.createdBy?.naam || entry?.createdBy?.email || "-"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(entry.status)}`}>
                          {formatStatusLabel(entry.status || "active")}
                        </span>
                      </td>
                    </tr>
                  ))}
                />

                <DossierTable
                  title="Inspectierapporten"
                  count={inspectionDocuments.length}
                  loading={documentsLoading}
                  emptyMessage="Nog geen inspectierapporten in de documentbibliotheek."
                  columns={["Titel", "Status", "Categorie", "Laatste wijziging"]}
                  rows={inspectionDocuments.map((item) => (
                    <tr key={item._id}>
                      <td className="px-4 py-3 font-medium text-slate-900">{item.title || "-"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(item.status)}`}>
                          {formatStatusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{getDocumentCategoryLabel(item)}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDate(item.updatedAt || item.createdAt)}</td>
                    </tr>
                  ))}
                />
              </div>
            ) : null}

            {activeTab === "historie" ? (
              <DossierTable
                title="Dossierhistorie"
                count={historyRows.length}
                emptyMessage="Nog geen gebeurtenissen in dit dossier."
                columns={["Datum", "Bron", "Onderwerp", "Detail", "Status", "Actor"]}
                rows={historyRows.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-3 text-slate-600">{formatDate(row.date)}</td>
                    <td className="px-4 py-3 text-slate-700">{row.source}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.title}</td>
                    <td className="px-4 py-3 text-slate-700">{row.detail}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${statusTone(row.status)}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.actor}</td>
                  </tr>
                ))}
              />
            ) : null}

            {activeTab === "toegang" ? (
              <div className="space-y-6">
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <tbody className="divide-y divide-slate-200 bg-white">
                      <AccessRow label="Service-ID" value={installation.serviceId || "-"} />
                      <AccessRow label="Service-object status" value={serviceStatus} />
                      <AccessRow label="Logboek Access" value={formatAccessState(installation)} />
                      <AccessRow label="QR-token" value={installation.qrToken || "-"} monospace />
                      <AccessRow label="QR-route" value={accessUrl || "-"} monospace />
                      <AccessRow label="Installatie-id" value={installation._id || "-"} monospace />
                      <AccessRow label="Aangemaakt" value={formatDate(installation.createdAt)} />
                      <AccessRow label="Laatst bijgewerkt" value={formatDate(installation.updatedAt)} />
                    </tbody>
                  </table>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <table className="min-w-full divide-y divide-slate-200 text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Locatiecontext</th>
                        <th className="px-4 py-3">Waarde</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      <AccessRow label="Adres" value={formatLocationLabel(installation)} asCells />
                      <AccessRow
                        label="Gebruiksfunctie"
                        value={formatGebruiksdoelen(installation?.bagAddress?.gebruiksdoelen)}
                        asCells
                      />
                      <AccessRow label="Oppervlakte" value={formatArea(installation?.bagAddress?.oppervlakte)} asCells />
                      <AccessRow
                        label="Woonplaats"
                        value={installation?.bagAddress?.woonplaatsNaam || "-"}
                        asCells
                      />
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <aside className="space-y-4 xl:sticky xl:top-32">
          <section className="rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">
                Service-ID en status
              </h3>
            </div>
            <div className="space-y-4 px-5 py-5">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Service-ID</p>
                <p className="mt-1 break-all font-mono text-sm text-slate-900">
                  {installation.serviceId || "Nog geen Service-ID"}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Toegang</p>
                  <p className="mt-1 font-medium text-slate-900">{formatAccessState(installation)}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Service-status</p>
                  <p className="mt-1 font-medium text-slate-900">{serviceStatus}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">QR-code</h3>
            </div>
            <div className="space-y-4 px-5 py-5">
              {qrCodeUrl ? (
                <img
                  src={qrCodeUrl}
                  alt={`QR-code voor ${installationName}`}
                  className="mx-auto block w-full max-w-[220px] rounded-xl border border-slate-200 bg-white p-3"
                />
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500">
                  QR-code nog niet beschikbaar.
                </div>
              )}
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">QR-route</p>
                <p className="mt-1 break-all font-mono text-xs text-slate-700">{accessUrl || "-"}</p>
              </div>
            </div>
          </section>

          <section className="rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">Snelle acties</h3>
            </div>
            <div className="space-y-3 px-5 py-5">
              <button
                type="button"
                onClick={() => handleTabChange("werkbonnen")}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <span>Bekijk werkbonnen</span>
                <span className="text-slate-400">{openPlannedWorkorders.length + historicalLogbookWorkorders.length}</span>
              </button>
              <button
                type="button"
                onClick={() => handleTabChange("documenten")}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <span>Open documenten</span>
                <span className="text-slate-400">{documents.length}</span>
              </button>
              <button
                type="button"
                onClick={() => handleTabChange("inspecties")}
                className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <span>Inspecties</span>
                <span className="text-slate-400">{inspectionEntries.length}</span>
              </button>
              {(accessUrl || installation.qrToken) ? (
                <button
                  type="button"
                  onClick={() => handleCopy(accessUrl || installation.qrToken, "QR-route gekopieerd.")}
                  className="flex w-full items-center justify-between rounded-xl border border-slate-200 px-3 py-3 text-left text-sm text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  <span>Kopieer QR-route</span>
                  <span className="text-slate-400">Kopieer</span>
                </button>
              ) : null}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

function DossierTable({ title, count, columns, rows, emptyMessage, loading = false }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-700">{title}</h3>
        <span className="text-xs font-medium text-slate-500">{count}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-white text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
            <tr>
              {columns.map((column) => (
                <th key={column} className="px-4 py-3">
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-slate-500">
                  Laden...
                </td>
              </tr>
            ) : rows.length ? (
              rows
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-sm text-slate-500">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AccessRow({ label, value, monospace = false, asCells = false }) {
  if (asCells) {
    return (
      <tr>
        <td className="px-4 py-3 text-slate-500">{label}</td>
        <td className={`px-4 py-3 text-slate-900 ${monospace ? "font-mono text-xs" : ""}`}>{value}</td>
      </tr>
    );
  }

  return (
    <tr>
      <th className="w-1/3 px-4 py-3 text-left text-slate-500">{label}</th>
      <td className={`px-4 py-3 text-slate-900 ${monospace ? "font-mono text-xs" : ""}`}>{value}</td>
    </tr>
  );
}
